import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './FloorScales.css';
import { useProducts, type FloorScalesSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const FloorScales: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: models, loading, error } = useProducts<FloorScalesSpecs>('floor-scales', editMode);
  const navigate = useNavigate();

  const additionalModels = [
    'Báscula de Piso para Aplicaciones Especiales',
    'Sistemas de Pesaje Integrados',
    'Soluciones Personalizadas de Pesaje'
  ];

  const applications = [
    {
      title: 'Manufactura',
      description: 'Pesaje de productos, materiales y componentes en líneas de producción.',
    },
    {
      title: 'Almacenes',
      description: 'Control de inventario y verificación de peso de mercancías.',
    },
    {
      title: 'Logística',
      description: 'Pesaje de paquetes, pallets y contenedores para envío.',
    },
    {
      title: 'Agricultura',
      description: 'Pesaje de productos agrícolas, fertilizantes y equipos.',
    },
    {
      title: 'Química',
      description: 'Pesaje de materias primas y productos químicos.',
    },
    {
      title: 'Alimentaria',
      description: 'Pesaje higiénico de ingredientes y productos alimentarios.',
    },
  ];

  const advantages = [
    { title: 'Instalación Simple',   description: 'Diseño sin fosa que permite instalación rápida sobre superficie.' },
    { title: 'Acceso Fácil',         description: 'Rampas integradas o opcionales para fácil acceso de equipos.' },
    { title: 'Construcción Robusta', description: 'Materiales de alta calidad para uso industrial intensivo.' },
    { title: 'Protección Integrada', description: 'Topes de sobrecarga protegen las celdas de carga.' },
    { title: 'Versatilidad',         description: 'Múltiples tamaños y capacidades para diversas aplicaciones.' },
    { title: 'Mantenimiento',        description: 'Acceso fácil para limpieza y servicios de mantenimiento.' },
  ];

  return (
    <div className="fls-page">
      {error && <div className="fls-status-bar fls-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="fls-hero">
        <div className="container fls-hero-content">
          <nav className="fls-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="fls-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="fls-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Básculas de Piso</span>
          </nav>
          <h1 className="fls-hero-title">Básculas de Piso</h1>
          <p className="fls-hero-subtitle">
            Gran selección de básculas de piso de marcas líderes como Avery Weigh-Tronix
            para aplicaciones industriales de alta demanda.
          </p>
          {authType === 'internal' && (
            <button
              className={`fls-btn-edit-mode${editMode ? ' fls-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="fls-hero-image" aria-hidden="true">
          <img src="/images/platform-scales.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="fls-overview">
        <div className="container">
          <div className="fls-overview-grid">
            <div className="fls-overview-text">
              <span className="fls-label">Descripción general</span>
              <h2>Básculas de Piso Industriales</h2>
              <p>
                Contamos con una gran selección de básculas de piso, representando
                a Avery Weigh-Tronix y otras marcas líderes. Nuestras básculas de piso ofrecen
                soluciones versátiles para una amplia gama de aplicaciones de pesaje industrial.
              </p>
              
            </div>
            <div className="fls-overview-image">
 <ul className="fls-features-list" aria-label="Características principales">
                {[
                  'Diseños de perfil bajo sin requerimiento de fosa',
                  'Construcción en acero al carbón y acero inoxidable',
                  'Capacidades desde 250 kg hasta más de 50,000 kg',
                  'Rampas de acceso integradas o opcionales',
                  'Topes de sobrecarga para protección de celdas',
                  'Uso en interiores y exteriores',
                ].map((item, i) => (
                  <li key={i} className="fls-feature-item">
                    <svg className="fls-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="fls-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos Disponibles</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/floor-scales')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : models.map((model, index) => (
                <ProductCard
                  key={index}
                  reviewed={model.reviewed}
                  endOfLife={model.endOfLife}
                  productId={model.productId}
                  name={model.name}
                  badge={model.specs?.model ?? undefined}
                  description={model.specs?.description ?? undefined}
                  image={model.image}
                  onRevisar={editMode && model.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(model.specs?.rampOptions?.length ? [{ icon: '🔼', label: 'Opciones de rampa', items: (model.specs?.rampOptions ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.capacities?.length  ? [{ icon: '📦', label: 'Capacidades',       items: (model.specs?.capacities  ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.features?.length    ? [{ icon: '⚙️', label: 'Características',   items: (model.specs?.features    ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Additional models ────────────────────────────────── */}
      <section className="fls-additional">
        <div className="container">
          <span className="fls-label">Más opciones</span>
          <h2 className="fls-section-title">Modelos Adicionales</h2>
          <p className="fls-section-intro">
            Además de los modelos estándar, ofrecemos soluciones a la medida
            para requerimientos especiales de su operación.
          </p>
          <div className="fls-additional-grid">
            {additionalModels.map((model, index) => (
              <div key={index} className="fls-additional-card">
                <h3>{model}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="fls-applications">
        <div className="container">
          <span className="fls-label fls-label--light">Industrias</span>
          <h2 className="fls-section-title fls-section-title--light">Aplicaciones</h2>
          <div className="fls-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="fls-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Advantages ───────────────────────────────────────── */}
      <section className="fls-advantages">
        <div className="container">
          <div className="fls-advantages-inner">
            <div className="fls-advantages-header">
              <span className="fls-label">Por qué elegirnos</span>
              <h2 className="fls-section-title">Ventajas de Nuestras Básculas de Piso</h2>
              <p>
                Diseñadas para resistir las condiciones más exigentes
                de la industria con un mantenimiento mínimo.
              </p>
            </div>
            <ul className="fls-advantage-list" aria-label="Ventajas principales">
              {advantages.map((adv, index) => (
                <li key={index} className="fls-advantage-item">
                  <svg className="fls-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>
                    <strong>{adv.title}</strong> — {adv.description}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FloorScales;
